import { isNil, isUndefined } from 'lodash';
import React, { useEffect, useState } from 'react';
import { FancyRadio } from '../../../Components/FancyRadio';
import { FinalButton } from '../../../Components/FinalButton';
import { isMantraProvider, useCurrentProvider } from '../../../Components/Permissions';
import { supportLinkAnchorProps, useSupportLink } from '../../../Components/SupportLink';
import { Warning } from '../../../Components/Warning';
import { Text } from '../../../globalStyles';
import { findStatusByCareType } from '../../../utils/careTypeAppointmentStatuses';
import { getFullName } from '../../../modelUtils/users';
import { WizardContentWithBack } from '../Common';
import { BookingWizardProps } from '../types';
import { AppointmentType } from '../../../graphQL';
import { useCareTypeConfigContext } from '../../../Components/CareTypeConfigContext';

export function SelectAppointmentType({
  data: wizardData,
  prevStep,
  nextStep,
}: BookingWizardProps) {
  const { getCareTypeDisplay } = useCareTypeConfigContext();
  const supportLink = useSupportLink();
  const { currentProvider } = useCurrentProvider();
  const isCaregiver = isMantraProvider(currentProvider);
  const currentProviderCareTypes = new Set(currentProvider.careTypes);

  const { appointmentTemplates, patient, appointment } = wizardData;
  const { careFlows, careTypeAppointmentStatuses } = patient;
  const [warningMessage, setWarningMessage] = useState('');

  const [activeTemplateIdx, setActiveTemplateIdx] = useState<number | null>(
    !isNil(appointment.templateIndex) && appointmentTemplates[appointment.templateIndex]
      ? appointment.templateIndex
      : null
  );

  const activeTemplate = isNil(activeTemplateIdx) ? null : appointmentTemplates[activeTemplateIdx];

  const careEnabled = careFlows.some(
    f => f.canBookAppointment && (isNil(activeTemplate) || activeTemplate.careType === f.careType)
  );

  const hasState = Boolean(patient.primaryAddressState);
  const canBook = careEnabled && hasState;

  const nameAndId = `${getFullName(patient)} (${patient.customerId})`;

  const appointmentTypeOptions = appointmentTemplates.map((t, templateIdx) => ({
    id: templateIdx,
    label: `${t.description} (${t.duration} min)`,
    hidden: isCaregiver && !currentProviderCareTypes.has(t.careType),
  }));

  useEffect(() => {
    if (patient.careTypes.length > 1) return;

    const careType = patient.careTypes[0];
    const status = careType
      ? findStatusByCareType(careTypeAppointmentStatuses, careType)
      : undefined;

    // If a patient has already completed an initial appointment or has an appointment scheduled
    if (careType && (status?.upcomingAppointment || status?.completedAppointment)) {
      const checkinIdx = appointmentTemplates.findIndex(
        template => template.appointmentType === 'checkin' && template.careType === careType
      );
      setActiveTemplateIdx(checkinIdx);
    }
    // default to first intake
    else {
      const anyIntake = appointmentTemplates.findIndex(
        template => template.appointmentType === AppointmentType.Intake
      );
      setActiveTemplateIdx(anyIntake);
    }
  }, [patient.careTypes, setActiveTemplateIdx, appointmentTemplates, careTypeAppointmentStatuses]);

  useEffect(() => {
    setWarningMessage('');

    if (isNil(activeTemplateIdx) || isNil(appointmentTemplates[activeTemplateIdx])) {
      return;
    }

    const template = appointmentTemplates[activeTemplateIdx];
    const status = findStatusByCareType(careTypeAppointmentStatuses, template.careType);
    const hasExistingAppointment = !!(status?.upcomingAppointment || status?.completedAppointment);

    if (template.appointmentType === 'intake' && hasExistingAppointment) {
      setWarningMessage(
        `This patient already has a completed or upcoming ${
          getCareTypeDisplay(template.careType).appointmentIntakeLabel
        }.`
      );
    } else if (template.appointmentType === 'checkin' && !hasExistingAppointment) {
      setWarningMessage(
        `This patient has not yet had a ${
          getCareTypeDisplay(template.careType).appointmentIntakeLabel
        } completed or scheduled.`
      );
    }
  }, [activeTemplateIdx, careTypeAppointmentStatuses, appointmentTemplates, getCareTypeDisplay]);

  return (
    <WizardContentWithBack onBack={() => prevStep()}>
      <div className="mb3">
        <Text.h2>What type of appointment?</Text.h2>
        {canBook && (
          <Text.body className="mt2">
            You&apos;re booking an appointment for
            <strong> {nameAndId}</strong>
          </Text.body>
        )}
        {!hasState && (
          <Text.body className="mt2">
            Unable to book because <strong>{nameAndId}</strong> has no state address. To resolve
            this, have the patient complete onboarding, or{' '}
            <a {...supportLinkAnchorProps(supportLink)}>contact us</a> to update their state.
          </Text.body>
        )}
        {!careEnabled && (
          <Text.body className="mt2">
            Unable to book. Please activate care for <strong>{nameAndId}</strong> before proceeding.
          </Text.body>
        )}
      </div>
      <FancyRadio
        value={isNil(activeTemplateIdx) ? undefined : activeTemplateIdx}
        onChange={setActiveTemplateIdx}
        options={appointmentTypeOptions.filter(t => !t.hidden)}
      />
      {warningMessage && (
        <Warning>
          <b>Note:</b> {warningMessage}
        </Warning>
      )}
      <FinalButton
        kind="primary"
        disabled={isUndefined(activeTemplateIdx) || !careEnabled}
        className="w-100 mt4"
        onClick={() =>
          nextStep({
            ...wizardData,
            appointment: {
              ...wizardData.appointment,
              templateIndex: activeTemplateIdx ?? undefined,
              duration: activeTemplate?.duration,
              careType: activeTemplate?.careType,
              appointmentType: activeTemplate?.appointmentType,
            },
          })
        }
      >
        Next: Select Date &amp; Time
      </FinalButton>
    </WizardContentWithBack>
  );
}
